import * as React from 'react'

import { useClientEditProfile } from './hooks'
import {
  CAlert,
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDatePicker,
  CForm,
  CFormCheck,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CMultiSelect,
  CRow,
} from '@coreui/react-pro'
import { Skeleton } from 'antd'
import {
  setClientBan,
  setClientDelete,
  setClientEditProfile,
  submitEditClient,
  submitEditClientCategory,
  updateClient,
} from './model'
import { cilFullscreen } from '@coreui/icons'
import CIcon from '@coreui/icons-react'
import { CommentList } from '../../../views/widgets/commentList'
import { CommentForm } from '../../../views/widgets/commentForm'
import { categoryList, languagesList } from './consts'
import { useUnit } from 'effector-react'
import { $countriesList } from '../../../features/dictionaries/model'
import { transformCountriesToOptions } from '../../../libs/formatCountries'
import { ApiResult } from '../../../components/apiResult/ApiResult'
import { Guard } from '../../../features/session/guard'
import { PERMISSIONS } from '../../../features/session/constants'

type Props = {
  className?: string
}

export const ClientEditProfile = ({ className }: Props) => {
  let { clientEditProfile, loading, error } = useClientEditProfile()
  const countriesList = useUnit($countriesList)

  const {
    clientId,
    firstName,
    middleName,
    email,
    lastName,
    country,
    address,
    phone,
    dateOfBirth,
    language,
    autoDepositDisabled,
    isViewStrategies,
    category,
  } = clientEditProfile

  const handleChange = (event: {
    currentTarget: { name: any; type: string; checked?: any; value: any }
  }) => {
    const value =
      event.currentTarget.type === 'checkbox'
        ? event.currentTarget.checked
        : event.currentTarget.value

    setClientEditProfile({
      ...clientEditProfile,
      [event.currentTarget.name]: value,
    })
  }

  const handleCountryChange = (objectKey: string, value: any) => {
    setClientEditProfile({
      ...clientEditProfile,
      [objectKey]: value,
    })
  }

  if (loading && !clientId) {
    return (
      <CRow>
        <CCol xs={12}>
          <CCard className="mb-4">
            <CCardHeader>
              <Skeleton.Input active size="small" style={{ width: 200 }} />
            </CCardHeader>
            <CCardBody>
              <Skeleton active paragraph={{ rows: 8 }} />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    )
  }

  return (
    <>
      <ApiResult
        effect={updateClient}
        successMessage="Client successfully updated"
      />
      <CCol xs={12} className={className}>
        <CCard className="mb-4">
          <CCardHeader className="d-sm-flex justify-content-between align-items-center">
            <strong>Edit client {clientId}</strong>
            <CButton
              color="secondary"
              className="me-2"
              title={'<- Back'}
              href={'/client/' + clientId}
            >
              <CIcon icon={cilFullscreen} />
            </CButton>
          </CCardHeader>
          <CCardBody>
            <CForm>
              <CRow>
                <CCol xs={12}>
                  <div className="mb-3">
                    <CFormLabel htmlFor="email">Email address</CFormLabel>
                    <Guard
                      permission={PERMISSIONS.EDIT_CLIENT_EMAIL}
                      mode="permission"
                    >
                      <CFormInput
                        type="email"
                        id="email"
                        name="email"
                        placeholder="name@example.com"
                        value={email || ''}
                        onChange={handleChange}
                      />
                    </Guard>
                  </div>
                </CCol>
              </CRow>
              <CRow>
                <CCol xs={4}>
                  <div className="mb-3">
                    <CFormLabel htmlFor="firstName">First name</CFormLabel>
                    <CFormInput
                      type="text"
                      id="firstName"
                      name="firstName"
                      placeholder="first name"
                      value={firstName}
                      onChange={handleChange}
                    />
                  </div>
                </CCol>
                <CCol xs={4}>
                  <div className="mb-3">
                    <CFormLabel htmlFor="middleName">Middle name</CFormLabel>
                    <CFormInput
                      type="text"
                      id="middleName"
                      name="middleName"
                      placeholder="Middle name"
                      value={middleName || ''}
                      onChange={handleChange}
                    />
                  </div>
                </CCol>
                <CCol xs={4}>
                  <div className="mb-3">
                    <CFormLabel htmlFor="lastName">Last name</CFormLabel>
                    <CFormInput
                      type="text"
                      id="lastName"
                      name="lastName"
                      placeholder="last name"
                      value={lastName || ''}
                      onChange={handleChange}
                    />
                  </div>
                </CCol>
              </CRow>
              <CRow>
                <CCol xs={4}>
                  <div className="mb-3">
                    <CMultiSelect
                      id="country"
                      label="Country"
                      multiple={false}
                      options={transformCountriesToOptions(countriesList).map(
                        (item) => {
                          if (country == item.value) {
                            return { ...item, selected: true }
                          } else {
                            return { ...item, selected: false }
                          }
                        },
                      )}
                      onChange={(event) => {
                        let country: (string | number)[] = []
                        event.forEach((item) => country.push(item.value))
                        handleCountryChange('country', country.pop())
                      }}
                      virtualScroller
                    />
                  </div>
                </CCol>
                <CCol xs={4}>
                  <div className="mb-3">
                    <CFormLabel htmlFor="address">Address</CFormLabel>
                    <CFormInput
                      type="text"
                      id="address"
                      name="address"
                      placeholder="Delaware, Mohrborough, 802 Heloise Neck"
                      value={address || ''}
                      onChange={handleChange}
                    />
                  </div>
                </CCol>
                <CCol xs={4}>
                  <div className="mb-3">
                    <CFormLabel htmlFor="address">Phone</CFormLabel>
                    <CFormInput
                      type="text"
                      id="phone"
                      name="phone"
                      placeholder="1-234-567-8912"
                      value={phone || ''}
                      onChange={(event) => {
                        let phone = event.currentTarget.value
                        if (phone && !phone.startsWith('+')) {
                          phone = '+' + phone
                        }
                        handleChange({
                          currentTarget: {
                            name: 'phone',
                            type: 'text',
                            value: phone,
                          },
                        })
                      }}
                    />
                  </div>
                </CCol>
              </CRow>
              <CRow>
                <CCol xs={4}>
                  <div className="mb-3">
                    <CFormLabel htmlFor="country">Date of birth</CFormLabel>
                    <CDatePicker
                      locale="en-US"
                      format={'yyyy-MM-dd'}
                      date={dateOfBirth || ''}
                      onDateChange={(date, formatedDate) =>
                        handleChange({
                          currentTarget: {
                            name: 'dateOfBirth',
                            type: 'dateOfBirth',
                            value: formatedDate,
                          },
                        })
                      }
                    />
                  </div>
                </CCol>
                <CCol xs={4}>
                  <CFormSelect
                    label="Language"
                    name="language"
                    value={language || ''}
                    onChange={handleChange}
                  >
                    <option>Choose language</option>
                    {languagesList.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </CFormSelect>
                </CCol>
                <CCol xs={4}>
                  <CFormCheck
                    id="autoDepositDisabled"
                    name="autoDepositDisabled"
                    checked={autoDepositDisabled || false}
                    onChange={handleChange}
                    label="Auto deposit disabled"
                  />
                </CCol>
                <CCol xs={4} className="mb-3">
                  <CFormCheck
                    name="isViewStrategies"
                    checked={isViewStrategies}
                    onChange={handleChange}
                    label="Allow to view all information about strategies"
                  />
                </CCol>
                <CCol xs={4}>
                  <CFormSelect
                    label="Category"
                    name="category"
                    value={category || ''}
                    onChange={handleChange}
                    placeholder="Change category"
                  >
                    {categoryList.map((item, index) => (
                      <option value={item.value} key={index}>
                        {item.label}
                      </option>
                    ))}
                  </CFormSelect>
                  <CButton
                    className="mb-3"
                    disabled={loading}
                    onClick={() => submitEditClientCategory(clientEditProfile)}
                  >
                    {loading && (
                      <span className="spinner-border spinner-border-sm"></span>
                    )}
                    Change category
                  </CButton>
                </CCol>
              </CRow>
              <CButton
                className="mb-3"
                disabled={loading}
                onClick={() => submitEditClient(clientEditProfile)}
              >
                {loading && (
                  <span className="spinner-border spinner-border-sm"></span>
                )}
                Save
              </CButton>
            </CForm>
          </CCardBody>
        </CCard>
      </CCol>
      <CCol sm={4}>
        <CRow>
          <CCol className="mb-2">Comments</CCol>
        </CRow>
        {clientId != '' && (
          <CommentList entityType="client" entityId={clientId} />
        )}
        <CommentForm type="client" entityId={clientId} />
      </CCol>
    </>
  )
}

export const ClientEditDetail = () => {
  return (
    <div>
      <CRow className="mb-3">
        <ClientEditProfile />
      </CRow>
      <CRow>
        <CButtonGroup role="group" aria-label="Client actions">
          <CButton color="warning" onClick={() => setClientBan()}>
            Ban
          </CButton>
          <CButton color="danger" onClick={() => setClientDelete()}>
            Delete
          </CButton>
        </CButtonGroup>
      </CRow>
    </div>
  )
}
